/**
 * FotografiasPorClienteView - Accordion view grouping photos by client
 *
 * Groups photos by clienteId, resolves client names from jobClientes,
 * and generates incremental display labels per client + classification.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronRight, Image as ImageIcon, FileText, Trash2, MapPin } from 'lucide-react';
import { Tooltip } from '@mantine/core';
import type { MockFoto } from '../../hooks/useFotografias';
import { AuthenticatedImage } from '../AuthenticatedImage';

interface FotografiasPorClienteViewProps {
  fotos: MockFoto[];
  jobClientes?: Array<{ id: number; nome_fantasia?: string; razao_social?: string }>;
  onPhotoClick: (index: number) => void;
  onDelete: (id: number) => void;
}

export function FotografiasPorClienteView({
  fotos,
  jobClientes,
  onPhotoClick,
  onDelete,
}: FotografiasPorClienteViewProps) {
  // Group photos by clienteId (0 → 'sem_cliente')
  const grouped = useMemo(() => {
    const groups: Record<string, MockFoto[]> = {};
    fotos.forEach((foto) => {
      const key = foto.clienteId > 0 ? String(foto.clienteId) : 'sem_cliente';
      if (!groups[key]) groups[key] = [];
      groups[key].push(foto);
    });
    return groups;
  }, [fotos]);

  const groupKeys = useMemo(() => Object.keys(grouped), [grouped]);

  // All sections start expanded
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set(groupKeys));

  // Update expanded set when groups change (new clients appear)
  useMemo(() => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [groupKeys]);

  const toggleClient = (key: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getClienteName = (clienteKey: string): string => {
    if (clienteKey === 'sem_cliente') return 'Sem Cliente';
    const cliente = jobClientes?.find((c) => c.id === Number(clienteKey));
    return cliente?.nome_fantasia || cliente?.razao_social || `Cliente #${clienteKey}`;
  };

  const getDisplayLabel = (foto: MockFoto, fotosDoCliente: MockFoto[]): string => {
    const clienteKey = foto.clienteId > 0 ? String(foto.clienteId) : 'sem_cliente';
    const clienteName = getClienteName(clienteKey).replace(/\s+/g, '_');
    const abrev = foto.classificacaoAbrev || foto.classificacao || 'Foto';
    const sameType = fotosDoCliente.filter(
      (f) => (f.classificacaoAbrev || f.classificacao) === (foto.classificacaoAbrev || foto.classificacao)
    );
    const index = sameType.indexOf(foto) + 1;
    const suffix = sameType.length > 1 ? `_${index}` : '';
    return `${clienteName}_${abrev}${suffix}`;
  };

  // Empty state
  if (groupKeys.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>Nenhuma foto encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupKeys.map((clienteKey) => {
        const clienteFotos = grouped[clienteKey];
        const expanded = expandedClients.has(clienteKey);
        return (
          <div key={clienteKey} className="bg-white rounded-lg border border-slate-200">
            {/* Header */}
            <button
              onClick={() => toggleClient(clienteKey)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors rounded-t-lg"
            >
              <div className="flex items-center gap-3">
                <ChevronRight
                  className={`w-4 h-4 text-slate-400 transform transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
                <span className="font-semibold text-slate-800">{getClienteName(clienteKey)}</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {clienteFotos.length} {clienteFotos.length === 1 ? 'foto' : 'fotos'}
                </span>
              </div>
            </button>

            {/* Photo grid (collapsible) */}
            {expanded && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {clienteFotos.map((foto) => {
                    const globalIndex = fotos.indexOf(foto);
                    return (
                      <FotoCardMini
                        key={foto.id}
                        foto={foto}
                        displayLabel={getDisplayLabel(foto, clienteFotos)}
                        onClick={() => onPhotoClick(globalIndex)}
                        onDelete={() => onDelete(foto.id)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== FOTO CARD MINI ====================

interface FotoCardMiniProps {
  foto: MockFoto;
  displayLabel: string;
  onClick: () => void;
  onDelete: () => void;
}

function FotoCardMini({ foto, displayLabel, onClick, onDelete }: FotoCardMiniProps) {
  const isPdf = foto.type === 'pdf';
  const hasRealImage = foto.arquivoUrl && !isPdf;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!hasRealImage || imageError) return;
    const raf = requestAnimationFrame(() => {
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setImageLoaded(true);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [hasRealImage, imageError]);

  useEffect(() => {
    if (!hasRealImage || imageLoaded || imageError) return;
    const timer = setTimeout(() => {
      if (!imageLoaded) setImageError(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [hasRealImage, imageLoaded, imageError]);

  return (
    <div className="relative group">
      {/* Card */}
      <button
        onClick={onClick}
        className={`w-full aspect-square rounded-lg border-2 overflow-hidden transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isPdf
            ? 'bg-red-50 border-red-100 hover:border-red-300'
            : 'bg-slate-100 border-slate-200 hover:border-blue-400'
        }`}
        aria-label={`Ver ${displayLabel}`}
      >
        <div className="w-full h-full flex flex-col items-center justify-center p-2 relative">
          {isPdf ? (
            <>
              <FileText className="w-8 h-8 text-red-400 mb-1" />
              <span className="text-[10px] font-medium text-slate-700 truncate max-w-full px-1">
                {foto.fileName}
              </span>
            </>
          ) : hasRealImage && !imageError ? (
            <>
              {!imageLoaded && (
                <ImageIcon className="w-8 h-8 text-slate-300 animate-pulse absolute" />
              )}
              <AuthenticatedImage
                src={foto.arquivoUrl}
                alt={displayLabel}
                className={`w-full h-full object-cover ${imageLoaded ? '' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-slate-400 mb-1" />
              <span className="text-[10px] font-medium text-slate-700 truncate max-w-full px-1">
                {foto.fileName}
              </span>
            </>
          )}
        </div>
      </button>

      {/* GPS Badge (top left) */}
      {foto.hasGps && (
        <div className="absolute top-1 left-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center shadow-sm">
          <MapPin className="w-3 h-3" />
        </div>
      )}

      {/* Delete Button (hover) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-400"
        aria-label={`Excluir ${displayLabel}`}
      >
        <Trash2 className="w-3 h-3" />
      </button>

      {/* Classification Badge (bottom center overlay for loaded images) */}
      {hasRealImage && !imageError && imageLoaded && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
          <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-600/80 text-white">
            {foto.classificacaoAbrev}
          </span>
        </div>
      )}

      {/* Display Label (bottom) */}
      <div className="mt-1 text-[10px] text-slate-600 font-medium truncate text-center" title={displayLabel}>
        {displayLabel}
      </div>
    </div>
  );
}

export default FotografiasPorClienteView;
